<template>
  <div class="v2-retailer-cart-list v2-p-4">
    <!-- Search section -->
    <div class="v2-w-full">
      <V2CartSearchSaveClearSection
        :cart-list="cartList"
        :is-saving-cart="isSavingCart || isClearingCart"
        @on-search-cart-products="onSearchCartProducts"
        @on-click-save-cart="onClickSaveCart"
        @on-click-clear-cart="handleClearCart"
        @on-selected-store="onSelectedStore"
      />
    </div>
    <!-- Cart list loop -->
    <!--
      Important: this list intentionally does NOT pass a filtered
      cart.products subset on search and does NOT use a `:key="reloadKey"`
      forced remount. Either of those caused V2CartProducts to splice and
      re-init its productsSlice, which synchronously unmounts every
      already-mounted <b-table>/<b-form-input> in the cart — the original
      "Page Unresponsive" freeze. Instead, the active search text is
      forwarded down through V2StoreCart to V2CartProducts which applies it
      via its existing v-show / displayedProducts mechanism (no unmount).
    -->
    <div class="v2-w-full">
      <div
        v-for="(cart, index) in filteredCartList"
        :key="'cart' + cart._id"
      >
        <template v-if="!!cart.products.length">
          <hr class="v2-retailer-cart-list__divider">
          <V2StoreCart
            :cart="cart"
            :external-search-text="currentSearchText"
            @on-updated-cart-values="onUpdatedCartValues($event, index)"
          />
        </template>
      </div>
    </div>
    <V2ClearCartsModal
      :show="showClearModal"
      @on-close-modal="onCloseClearModal"
      @on-click-clear-cart="onClickClearCart"
    />
  </div>
</template>

<script>
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { DELETE_CARTS, UPDATE_CARTS } from '@/store/modules/checkout-v2.module'
import { GET_COMMON_BASE } from '@/store/modules/common.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import _ from 'lodash'
import { trackClearCartModal } from '@/views/apps/checkout-v2/trackMultipleStoreCartHelpers'
import V2CartSearchSaveClearSection from './V2CartSearchSaveClearSection.vue'
import V2StoreCart from './V2StoreCart.vue'
import V2ClearCartsModal from './V2ClearCartsModal.vue'

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'V2RetailerCartList',
  components: {
    V2StoreCart,
    V2CartSearchSaveClearSection,
    V2ClearCartsModal,
  },
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    loadCarts: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      cartValuesList: [],
      filteredCartList: [],
      isSavingCart: false,
      isClearingCart: false,
      showClearModal: false,
      currentStoreFilter: null,
      currentSearchText: '',
    }
  },
  emits: ['get-cart-values-list'],
  watch: {
    cartList: {
      handler() {
        this.applyStoreFilter()
      },
    },
  },
  created() {
    this.$store.dispatch(GET_COMMON_BASE)
    this.applyStoreFilter()
  },
  methods: {
    applyStoreFilter() {
      const storeId = this.currentStoreFilter
      this.filteredCartList = storeId
        ? this.cartList.filter(cart => cart._id === storeId)
        : this.cartList
    },
    handleClearCart() {
      this.showClearModal = true
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_CLEAR_CART_BUTTON, { carts: this.cartValuesList })
    },
    onCloseClearModal() {
      this.showClearModal = false
      trackClearCartModal('close', this.cartList)
    },
    onSelectedStore(storeId) {
      this.currentStoreFilter = storeId || null
      this.applyStoreFilter()
    },
    onSearchCartProducts: _.debounce(function onSearchCartProducts(val) {
      this.onSearchCartProductsByText(val)
    }, 300),
    onSearchCartProductsByText(searchText = '') {
      // Forward the search text to children via prop — V2CartProducts will
      // mirror it onto its internal `searchText` ref so its existing
      // v-show / displayedProducts logic does the filtering without
      // touching cart.products (which would trigger a mass remount).
      this.currentSearchText = searchText
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_SEARCH_PRODUCTS_IN_CART, { searchText })
    },
    async onClickSaveCart() {
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_SAVE_CART_BUTTON, { carts: this.cartValuesList })
      this.isSavingCart = true
      await this.$store.dispatch(UPDATE_CARTS).then(res => {
        apiToastSuccess(res.data.message)
        this.isSavingCart = false
        analytics.track(CART_WITH_MULTIPLE_STORES.CART_SAVED_SUCCESSFULLY, { carts: this.cartValuesList })
      })
      .catch(err => {
        apiToastError(err)
        this.isSavingCart = false
        analytics.track(CART_WITH_MULTIPLE_STORES.GETTING_ERROR_WHILE_SAVING_CART, err?.response)
      })
    },
    onClickClearCart() {
      this.showClearModal = false
      trackClearCartModal('confirm', this.cartList)
      this.isClearingCart = true
      const storeIds = this.filteredCartList.map(cart => cart.userId)
      this.$store.dispatch(DELETE_CARTS, storeIds).then(res => {
        apiToastSuccess(res.data.message)
        this.isClearingCart = false
        this.loadCarts()
        trackClearCartModal('success', this.cartList, { response: res })
      })
      .catch(err => {
        apiToastError(err)
        this.isClearingCart = false
        trackClearCartModal('failed', this.cartList, { error: err })
      })
    },
    onUpdatedCartValues(cartValues, index) {
      this.cartValuesList[index] = cartValues
      this.$emit('get-cart-values-list', this.cartValuesList)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-retailer-cart-list {
  &__divider {
    border: none;
    border-top: 1px solid var(--v2-border);
    margin: 1rem 0;
  }
}
</style>
